// Keyword hygiene — deterministic filters used by the Campaign Push Wizard's
// ENRICH step to strip the junk before Claude clusters into ad groups.
//
// Applied in this order:
//   1. Global-brand blocklist (US/EU water-treatment brands most Indian
//      clients can't/shouldn't bid on) — plus anything the user typed
//      in the wizard's own "Competitor URLs" or brand field.
//   2. SKU / product-code regex — catches things like `ed2000`, `pse1800`,
//      `wcs45kg`, `b00xajk0s0` that are almost always someone else's SKU.
//   3. Informational / navigational tokens — "how to", "what is", "vs",
//      "review", "meaning", "definition", "diy", "free", "salary", "jobs",
//      "login", "amazon", "flipkart", "wikipedia", "youtube", "reddit",
//      "quora", "menards", "ebay", "etsy", "pdf", "manual".
//
// The user can toggle the filter off if they want the raw enrichment.

const GLOBAL_BRAND_BLOCKLIST = [
    // US / EU water-treatment brands that rarely translate to Indian searches
    "culligan",
    "ispring",
    "morton",
    "pentair",
    "aquasana",
    "watts",
    "fleck",
    "kinetico",
    "harvey",
    "friedrich",
    "waterlogic",
    "aquasure harmony", // Aquasure Harmony is a US product
    "ecowell",
    "ge appliances",
    "whirlpool water",
    "ao smith", // US brand present in India but frequently a keyword-stealing rival
    "waterboss",
    "nuvoh2o",
    "eddy",
    "ecowater",
    "rainsoft",
    "waterdrop",
];

const INFORMATIONAL_TOKENS = [
    // multi-word phrases first — checked with includes()
    "how to",
    "how does",
    "how do",
    "how much",
    "how many",
    "how long",
    "how often",
    "what is",
    "what are",
    "why is",
    "why does",
    "vs ",
    "versus",
    "difference between",
    "types of",
    "meaning of",
    "which is best",
    "which is better",
];

const INFORMATIONAL_WORDS = [
    // single words — checked as whitespace-delimited tokens
    "meaning",
    "definition",
    "diy",
    "tutorial",
    "guide",
    "review",
    "reviews",
    "menards",
    "amazon",
    "flipkart",
    "wikipedia",
    "youtube",
    "reddit",
    "quora",
    "ebay",
    "etsy",
    "pdf",
    "manual",
    "template",
    "download",
    "torrent",
    "apk",
    "crack",
    "free",
    "jobs",
    "job",
    "salary",
    "vacancy",
    "hiring",
    "career",
    "login",
    "signin",
    "sign-in",
    "portal",
    "dashboard",
    "customer care",
    "toll free",
    "complaint",
    "scam",
    "lawsuit",
];

// SKU-ish patterns: 2-5 letters immediately followed by 3-6 digits, or an
// Amazon ASIN-style token (b0 + 8 alphanumeric).
const SKU_REGEX = /(\b[a-z]{1,5}\d{3,6}[a-z]?\b|\bb0[0-9a-z]{7,}\b|\bwcs\d+\b)/i;

const STANDARD_NEGATIVES = [
    // Marketplace / off-platform
    "amazon", "flipkart", "youtube", "wikipedia", "reddit", "quora", "ebay",
    "etsy", "menards", "aliexpress",
    // Non-commercial intent
    "how to", "what is", "why", "diy", "free", "download", "pdf", "manual",
    "meaning", "definition", "review", "reviews",
    // Hiring / careers
    "jobs", "job", "salary", "vacancy", "hiring", "career", "careers",
    // Account portals
    "login", "sign in", "portal", "dashboard", "customer care", "complaint",
    "toll free",
    // Piracy / warez
    "torrent", "apk", "crack", "template",
];

const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();

const containsPhrase = (kw, phrase) => kw.includes(phrase);
const containsWord = (kw, word) =>
    new RegExp(`(^|\\W)${word.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}(\\W|$)`).test(kw);

/**
 * Filter a list of enriched keywords, returning `{ kept, dropped, reasons }`.
 * @param {Array<{keyword: string}>} keywords
 * @param {Object} opts
 * @param {string[]} [opts.rivalBrands] — brands the user provided or that the
 *   distiller expanded (already lower-cased is fine; we normalise anyway)
 * @param {boolean} [opts.dropInformational=true]
 * @param {boolean} [opts.dropSkuCodes=true]
 * @param {boolean} [opts.dropGlobalBrands=true]
 * @param {boolean} [opts.dropRivalBrands=true]
 */
export const filterKeywordHygiene = (keywords, opts = {}) => {
    const {
        rivalBrands = [],
        dropInformational = true,
        dropSkuCodes = true,
        dropGlobalBrands = true,
        dropRivalBrands = true,
    } = opts;

    const rivalSet = new Set(
        (rivalBrands || []).map(norm).filter((s) => s && s.length > 1)
    );

    const reasons = {
        rival_brand: 0,
        global_brand: 0,
        sku_code: 0,
        informational: 0,
    };
    const kept = [];
    const dropped = [];

    for (const k of keywords) {
        const raw = k?.keyword || "";
        const kw = norm(raw);
        if (!kw) continue;

        let drop = null;

        if (
            dropRivalBrands &&
            [...rivalSet].some((b) => containsWord(kw, b) || kw.includes(b))
        ) {
            drop = "rival_brand";
        } else if (
            dropGlobalBrands &&
            GLOBAL_BRAND_BLOCKLIST.some((b) => kw.includes(b))
        ) {
            drop = "global_brand";
        } else if (dropSkuCodes && SKU_REGEX.test(kw)) {
            drop = "sku_code";
        } else if (dropInformational) {
            if (INFORMATIONAL_TOKENS.some((p) => containsPhrase(kw, p))) {
                drop = "informational";
            } else if (INFORMATIONAL_WORDS.some((w) => containsWord(kw, w))) {
                drop = "informational";
            }
        }

        if (drop) {
            reasons[drop] = (reasons[drop] || 0) + 1;
            dropped.push({ ...k, dropped_reason: drop });
        } else {
            kept.push(k);
        }
    }

    return { kept, dropped, reasons };
};

/**
 * Standard negatives that every campaign should have.
 * Accepts an extra list of rival brands to fold in.
 */
export const buildStandardNegatives = (rivalBrands = []) => {
    const rivals = (rivalBrands || [])
        .map(norm)
        .filter((s) => s && s.length > 1);
    return Array.from(new Set([...STANDARD_NEGATIVES, ...rivals]));
};

export const HYGIENE_REASON_LABELS = {
    rival_brand: "Rival brand",
    global_brand: "Global brand",
    sku_code: "SKU code",
    informational: "Informational",
};
